import { useEffect, useRef } from 'react'

import { SupportedChainId } from '~/constants/chains'
import {
  hasENSDomain,
  useAddressLookup,
  useENSLookup as useRqENSLookup,
} from '~/hooks/useENS'

type ENSLookupCallbacks = {
  onValidEns: (address: string) => void
  onInvalidEns: () => void
  onValidAddressWithEns: (ens: string) => void
}

// Resolves `text` as either an ENS name (forward) or an address (reverse) via
// React Query, firing the matching callback. Returns whether a lookup is in flight.
export const useENSLookup = (
  text: string,
  chainId: SupportedChainId,
  callbacks: ENSLookupCallbacks,
): boolean => {
  // Keep callbacks in a ref so the effects re-run only when the lookup result
  // changes, not when the (re-created) callbacks change.
  const callbacksRef = useRef(callbacks)
  callbacksRef.current = callbacks

  const {
    address: resolvedEnsAddress,
    isLoading: isResolvingEns,
    notFound: ensNotFound,
  } = useRqENSLookup(text, chainId)
  const { data: reverseEns, isLoading: isResolvingReverse } = useAddressLookup(
    text,
    chainId,
  )

  // Forward resolution (ENS name → address): mirror the resolved address back
  // into the form, or surface a not-found error.
  useEffect(() => {
    if (!hasENSDomain(text)) return
    if (resolvedEnsAddress) {
      callbacksRef.current.onValidEns(resolvedEnsAddress)
    } else if (ensNotFound) {
      callbacksRef.current.onInvalidEns()
    }
  }, [text, resolvedEnsAddress, ensNotFound])

  // Reverse resolution (address → ENS name): show the associated name.
  useEffect(() => {
    if (reverseEns) callbacksRef.current.onValidAddressWithEns(reverseEns)
  }, [reverseEns])

  return isResolvingEns || isResolvingReverse
}
